import * as THREE from 'three';
import type { RigCamera } from '../cameraRig';

// Up and to the left of the eye, as a camera-mounted headlight is in Fusion and
// Onshape: whichever side is turned to the viewer is lit, and still reads as 3D.
const KEY_OFFSET = new THREE.Vector3(-0.55, 0.95, 0.75).normalize();

export interface UnderLightSettings {
  color: string;
  intensity: number;
  /** Degrees round the vertical the light comes from. */
  around: number;
  /** Degrees below the floor, 90 being straight up. */
  tilt: number;
}

export const DEFAULT_UNDER_LIGHT: UnderLightSettings = { color: '#ffffff', intensity: 1.3, around: 0, tilt: 90 };

/** Where an under-light at these angles shines from, as a unit vector below the floor. */
export function underLightDirection({ around, tilt }: UnderLightSettings, target: THREE.Vector3): THREE.Vector3 {
  const a = THREE.MathUtils.degToRad(around);
  const t = THREE.MathUtils.degToRad(tilt);
  return target.set(Math.cos(a) * Math.cos(t), Math.sin(a) * Math.cos(t), -Math.sin(t));
}

export class ViewportLighting {
  private readonly key: THREE.DirectionalLight;
  private readonly under = new THREE.DirectionalLight(0xffffff, 0);
  private readonly offset = new THREE.Vector3();

  constructor(scene: THREE.Scene, isDark: boolean) {
    scene.add(
      new THREE.HemisphereLight(
        isDark ? 0xd4dae8 : 0xffffff,
        isDark ? 0x23232f : 0x3e4452,
        isDark ? 0.7 : 0.5,
      ),
    );
    this.key = new THREE.DirectionalLight(0xffffff, isDark ? 1.6 : 2.2);
    const top = new THREE.DirectionalLight(0xffffff, 0.35);
    top.position.set(0.3, 0.2, 1);
    scene.add(top);
    scene.add(this.key);
    scene.add(this.key.target);
    this.under.visible = false;
    scene.add(this.under, this.under.target);
  }

  setUnderLight(settings: UnderLightSettings | null): void {
    this.under.visible = settings !== null;
    if (!settings) return;
    this.under.color.set(settings.color);
    this.under.intensity = settings.intensity;
    underLightDirection(settings, this.under.position);
    this.under.target.position.set(0, 0, 0);
    this.under.updateMatrixWorld();
    this.under.target.updateMatrixWorld();
  }

  follow(camera: RigCamera, target: THREE.Vector3): void {
    this.offset.copy(KEY_OFFSET).applyQuaternion(camera.quaternion);
    this.key.position.copy(target).add(this.offset);
    this.key.target.position.copy(target);
    this.key.target.updateMatrixWorld();
  }
}
